<script lang="ts" setup>
import { SancareBreadcrumb } from '@sancare/typescript-commons'
import { HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'
import { computed, onMounted, Ref, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router'

import { useBreadcrumb } from '@/breadcrumbs/useBreadcrumb'
import ScrollToTopButton from '@/common/ScrollToTopButton.vue'
import { prefetchSmrStayDocuments } from '@/misc/stayDocuments'
import { prefetchSmrStayJustifications } from '@/misc/stayJustifications'
import { prefetchSmrStaySuggestions } from '@/misc/staySuggestions'
import { getSelectedChunks } from '@/stay-displayer/chunkJustifications'
import DocumentViewer from '@/stay-displayer/DocumentViewer.vue'
import GroupingCardSmr from '@/stay-displayer/grouping/smr/GroupingCardSmr.vue'
import ReportSelector from '@/stay-displayer/ReportSelector.vue'
import { useReadingListMode } from '@/stay-displayer/smr/composables/useReadingListMode'
import { refreshRhs, refreshStay, refreshStayContent } from '@/stay-displayer/smr/dataRefreshers'
import GeneralStayData from '@/stay-displayer/smr/GeneralStayData.vue'
import MedicalActs from '@/stay-displayer/smr/rhs/medical-act/MedicalActs.vue'
import SuggestedMedicalActs from '@/stay-displayer/smr/rhs/medical-act/SuggestedMedicalActs.vue'
import RehabilitationActs from '@/stay-displayer/smr/rhs/rehabilitation-act/RehabilitationActs.vue'
import RhsDependencies from '@/stay-displayer/smr/rhs/RhsDependencies.vue'
import RhsDiagnoses from '@/stay-displayer/smr/rhs/RhsDiagnoses.vue'
import RhsLoadingError from '@/stay-displayer/smr/rhs/RhsLoadingError.vue'
import { useStore } from '@/store'
import {
  RhsDashboardButtonLabel,
  RhsDashboardClass,
  RhsDashboardType,
  RhsLoadingErrorReason,
  SmrLoadingErrorReason
} from '@/store/modules/stay/smr/types'
import { formattedHealthData } from '@/store/modules/stay/types'

const { t } = useI18n()
const route = useRoute()
const store = useStore()
const router = useRouter()

const props = defineProps<{
  stayId: number
  rhsId: number
}>()

const mode = computed(() => store.state.settings.pmsi.mode)

const currentStay = computed(() => store.state.smrStay.currentStay)
const currentDocument = computed(() => store.state.smrStay.mainContent)
const justificationSelection = computed(() => store.state.smrStay.justificationSelection)
const scrollTarget = computed(() => store.state.smrStay.scrollTarget)
// Every suggestion type (search-matched or AI-predicted) comes from the backend-resolved
// suggestionJustifications (see chunkJustifications.ts::getSelectedChunks).
// todo: keyword from current search, see: https://app.clickup.com/t/86c0mw50c
const selectedChunks = computed(() => {
  if (!justificationSelection.value) {
    return null
  }

  const justification = currentStay.value.suggestionJustifications?.[`${justificationSelection.value.labelType}:${justificationSelection.value.labelReference}`] ?? null
  if (!justification) {
    return null
  }

  const documentIdByGeneratedDataId = new Map<string, number>([
    ...[...currentStay.value.reports, ...currentStay.value.textualHealthEntries]
      .filter((document) => Boolean(document.generatedDataId))
      .map((document): [string, number] => [document.generatedDataId, document.id]),
  ])

  return getSelectedChunks(justification, documentIdByGeneratedDataId)
})
const currentRhs = computed(() => store.state.smrStay.currentRhs)
const previousRhs = computed(() => {
  let previousRhs = null
  for (const rhs of store.state.smrStay.currentStay.rhsList) {
    if (rhs.id === props.rhsId) {
      return previousRhs
    }
    previousRhs = rhs
  }
  return previousRhs
})
const nextRhs = computed(() => {
  let foundCurrent = false
  for (const rhs of store.state.smrStay.currentStay.rhsList) {
    if (foundCurrent) {
      return rhs
    }
    if (rhs.id === props.rhsId) {
      foundCurrent = true
    }
  }
  return null
})
const predictedDiagnoses = computed(() => currentRhs.value.predictedLabels.filter((predictedLabel) => predictedLabel.diagnosis))

const healthData = computed(() => {
  return formattedHealthData(currentStay.value)
})
const stayMoneyGap = computed(() => isCurrentStayGmeSet.value
  ? currentStay.value.standardModeGroupingResult.value - currentStay.value.firstStandardModeGroupingResult.value
  : 0)

const initialStayModeGroupingResult = computed(() => currentStay.value.firstStandardModeGroupingResult)
const currentStayModeGroupingResult = computed(() => currentStay.value.standardModeGroupingResult)
const isCurrentStayGmeSet = computed(() => currentStayModeGroupingResult.value.gme !== null)
const displayedStayGroupingResult = computed(() => isCurrentStayGmeSet.value
  ? currentStayModeGroupingResult.value : initialStayModeGroupingResult.value
)

const refreshErrorReasons = { ...RhsLoadingErrorReason, ...SmrLoadingErrorReason }
const refreshErrorReason: Ref<RhsLoadingErrorReason | SmrLoadingErrorReason | null> = ref(null)
const isRefreshed: Ref<boolean> = ref(false)
const isLoading: Ref<boolean> = ref(false)
const isDocumentViewerVisible: Ref<boolean> = ref(false)

async function refresh() {
  refreshErrorReason.value = null
  isRefreshed.value = false

  // Fired alongside refreshStay (not after it resolves) so both requests run in parallel.
  const documentsPromise = prefetchSmrStayDocuments('id', String(props.stayId), store.state.stayList.currentSearch, store.commit)
  const justificationsPromise = prefetchSmrStayJustifications('id', String(props.stayId), store.commit)
  const suggestionsPromise = prefetchSmrStaySuggestions('id', String(props.stayId), store.commit)
  await refreshStay(String(props.stayId), 'id', store)
  await refreshStayContent(store, documentsPromise, justificationsPromise, suggestionsPromise)
  if (!currentStay.value.rhsList.some((rhs) => rhs.id === props.rhsId)) {
    throw new Error(RhsLoadingErrorReason.RHS_UNKNOWN)
  }
  await refreshRhs(props.rhsId, store)

  isRefreshed.value = true
}

onMounted(async () => {
  isLoading.value = true
  try {
    await refresh()
  } catch (e) {
    refreshErrorReason.value = Object.values(refreshErrorReasons).includes(e.message) ? e.message : null
  }
  isLoading.value = false
})

useReadingListMode('smrPage')

watch(() => props.rhsId, () => {
  store.commit('smrStay/setJustificationSelection', null)
})

watch(() => props.stayId, () => {
  store.commit('smrStay/setJustificationSelection', null)
})

function disableJustificationScrolling() {
  store.commit('smrStay/setScrollTarget', null)
}

function setMainContent(mainContent: HealthDataDocument | null) {
  store.commit('smrStay/setMainContent', mainContent)
}

function getRhsRoute(rhsId: number): RouteLocationRaw {
  const targetIndex = currentStay.value.rhsList.findIndex((rhs) => rhs.id === rhsId)
  return {
    name: 'rhs',
    params: { stayId: props.stayId, rhsId: rhsId },
    query: {
      ...route.query,
      rhsNumber: targetIndex + 1,
    },
  }
}

function navigateToRhs(rhsId: number) {
  store.commit('smrStay/setCurrentRhs', rhsId)
  router.push(getRhsRoute(rhsId))
}

const currentRhsDisplayNumber = computed(() => {
  if (!currentStay.value?.rhsList) {
    return null
  }
  const index = currentStay.value.rhsList.findIndex((rhs) => rhs.id === props.rhsId)
  return index >= 0 ? index + 1 : null
})

const { breadcrumbItems } = useBreadcrumb()

// Canonicalize the URL: sync ?stayLabel=... and ?rhsNumber=... with the loaded stay
// so the breadcrumb can render the right values without reading the store.
watchEffect(() => {
  const stayLabel = currentStay.value?.administrativeLocalStayId
  const rhsNumber = currentRhsDisplayNumber.value
  const newQuery = { ...route.query }
  let changed = false
  if (stayLabel !== null && stayLabel !== undefined && String(newQuery.stayLabel) !== String(stayLabel)) {
    newQuery.stayLabel = String(stayLabel)
    changed = true
  }
  if (rhsNumber !== null && rhsNumber !== undefined && String(newQuery.rhsNumber) !== String(rhsNumber)) {
    newQuery.rhsNumber = String(rhsNumber)
    changed = true
  }
  if (changed) {
    router.replace({ query: newQuery })
  }
})

function onRhsInputChange(e: Event) {
  const inputValue = Number((e.target as HTMLInputElement).value)
  if (
    inputValue >= 1
    && inputValue <= currentStay.value.rhsList.length
  ) {
    const rhs = currentStay.value.rhsList[inputValue - 1]
    if (rhs) {
      navigateToRhs(rhs.id)
    }
  } else {
    // Reset the input value if it's out of bounds
    (e.target as HTMLInputElement).value = String(currentRhsDisplayNumber.value)
  }
}
</script>

<template>
  <div class="container">
    <div
      v-if="isLoading"
      class="row align-items-center mt-5"
    >
      <div class="col-sm-12">
        <div
          class="loader loader-md loader-center mb-2"
        />
        <div
          class="text-center fst-italic"
        >
          Chargement des informations du RHS...
        </div>
      </div>
    </div>
    <div
      v-if="isRefreshed"
      class="row mt-1 mb-2"
    >
      <div
        id="rhs-navigation"
        class="col-md-12 d-flex justify-content-between"
      >
        <sancare-breadcrumb :items="breadcrumbItems" />
        <div class="d-flex justify-content-between align-items-center col-lg-5 col-md-8">
          <router-link
            v-if="previousRhs"
            id="go-to-previous-rhs"
            :to="getRhsRoute(previousRhs.id)"
            @click="navigateToRhs(previousRhs.id)"
          >
            &larr;&nbsp;Voir le RHS précédent
          </router-link>
          <span
            v-else
            id="go-to-previous-rhs"
            class="text-muted"
            aria-disabled="true"
          >
            &larr;&nbsp;Voir le RHS précédent
          </span>
          <div id="total-weeks">
            <input
              id="counter-week-number"
              type="number"
              min="1"
              :max="currentStay.rhsList.length"
              :value="currentRhsDisplayNumber"
              @change="onRhsInputChange"
            >
            / {{ currentStay.rhsList.length }}
          </div>
          <router-link
            v-if="nextRhs"
            id="go-to-next-rhs"
            :to="getRhsRoute(nextRhs.id)"
            @click="navigateToRhs(nextRhs.id)"
          >
            Voir le RHS suivant&nbsp;&rarr;
          </router-link>
          <span
            v-else
            id="go-to-next-rhs"
            class="text-muted"
            aria-disabled="true"
          >
            Voir le RHS suivant&nbsp;&rarr;
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="isRefreshed"
      class="row"
    >
      <div class="col-lg-7 col-md-12">
        <general-stay-data
          :stay="currentStay"
          :displayed-rhs="currentRhs"
        />
        <report-selector
          v-if="currentDocument == null"
          :health-data="healthData"
          :selected-chunks="selectedChunks"
          :main-content="currentDocument"
          :is-loading="isLoading"
          @set-main-content="setMainContent"
        />
        <div
          v-else
          class="mt-4"
        >
          <button
            class="btn btn-link btn-sm px-0 mb-2"
            @click="setMainContent(null)"
          >
            &larr;&nbsp;&nbsp;{{ t('stayDisplayer.mco.backToDocumentList') }}
          </button>
          <document-viewer
            :current-stay="currentStay"
            :current-document="currentDocument"
            :selection="justificationSelection"
            :selected-chunks="selectedChunks"
            :scroll-target="scrollTarget"
            :enable-false-justification-features="false"
            @disable-justification-scrolling="disableJustificationScrolling"
          />
        </div>
      </div>
      <div class="col-lg-5 col-md-12">
        <div class="row">
          <div class="col p-0">
            <grouping-card-smr
              :grouping-result="displayedStayGroupingResult"
              :money-gap="stayMoneyGap"
              :hospitalization-type="currentStay.hospitalizationType"
              :show-validator="false"
            />
          </div>
        </div>
        <div class="d-flex flex-column gap-2">
          <div class="row gap-2 flex-wrap flex-lg-nowrap">
            <rhs-diagnoses
              :rhs-id="currentRhs.id"
              :rhs-end-date="currentRhs.endDate"
              :current-mode-diagnosis-group="currentRhs.currentModeDiagnosisGroup"
              :predicted-diagnoses="predictedDiagnoses"
              :health-data="healthData"
              :suggestion-justifications="currentStay.suggestionJustifications"
              :stay-suggestions="currentStay.staySuggestions"
            />
          </div>
          <div class="row gap-2 flex-wrap flex-lg-nowrap">
            <rhs-dependencies
              :current-rhs="currentRhs"
              :previous-rhs="previousRhs"
              :health-data="healthData"
              :suggestion-justifications="currentStay.suggestionJustifications"
              :stay-suggestions="currentStay.staySuggestions"
            />
          </div>
          <div class="row gap-2 flex-wrap flex-lg-nowrap">
            <div class="col-lg-6 col-md-12 p-0">
              <div
                class="dashboard"
                :class="RhsDashboardClass[RhsDashboardType.RHS_DASHBOARD_ACT_CCAM]"
              >
                <medical-acts
                  :coded-acts="currentRhs.codedMedicalActs"
                  :rhs="currentRhs"
                />
              </div>
            </div>
            <div class="col-lg col-md-12 p-0">
              <div class="dashboard">
                <suggested-medical-acts
                  :rhs="currentRhs"
                  :health-data="healthData"
                  :suggestion-justifications="currentStay.suggestionJustifications"
                  :stay-suggestions="currentStay.staySuggestions"
                />
              </div>
            </div>
          </div>
          <div class="row gap-2 flex-wrap flex-lg-nowrap">
            <div class="col-lg-6 col-md-12 p-0">
              <div
                class="dashboard"
                :class="RhsDashboardClass[RhsDashboardType.RHS_DASHBOARD_ACT_CSARR]"
              >
                <rehabilitation-acts
                  :coded-rehabilitation-acts="currentRhs.codedRehabilitationActs"
                />
                <!-- TODO: https://app.clickup.com/t/2628858/WEB-2245 -->
                <div class="aligned-row mt-2">
                  <span v-tooltip="$t('comingSoon')">
                    <button
                      type="button"
                      class="col-12 btn btn-sm disabled"
                    >
                      {{ RhsDashboardButtonLabel[RhsDashboardType.RHS_DASHBOARD_ACT_CSARR] }}
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <scroll-to-top-button />
    </div>
    <rhs-loading-error
      v-else
      :reason="refreshErrorReason"
      :stay-id="stayId"
      :rhs-id="rhsId"
    />
  </div>
</template>

